import {Injectable, OnDestroy} from '@angular/core';
import {RemoteAppHtml5, RemoteAppReady} from '../models';
import {ComponentCleaner} from '../component-cleaner';
import {Observable, Observer} from 'rxjs';
import {R2CloudClientSubscriber} from '../ws/r2-cloud-client-subscriber';
import {USER_TOPIC} from '../../../main';
import {RemoteAppDaoService} from '../main/remote-app/remote-app-dao.service';
import {R2CloudStompService} from '../ws/r2-cloud-stomp.service';
import {CustomerGroupService} from '../main/customer-group/customer-group.service';
import {PdfPrintService} from '../main/my-prints/pdf-print.service';
import {RemoteAppReadyStoreService} from './indexedDB/remote-app-ready-store.service';
import {take} from 'rxjs/operators';
import {LoggerService} from './logger.service';
import {
    collectAllowedRemoteAppIds,
    isAuthoritativeRemoteAppList,
    isRemoteAppHtml5Eligible,
    reconcileRemoteAppList
} from '../remote-app-reconciliation-rules';

@Injectable({
    providedIn: 'root'
})
export class RemoteAppHtml5Service extends ComponentCleaner implements OnDestroy {
    private subscribers: R2CloudClientSubscriber<RemoteAppHtml5>[] = [];
    private status: RemoteAppHtml5[] = [];
    private currentRemoteAppReadyList: RemoteAppReady[] = [];

    private readonly handleOffline = (): void => {
        void this.tryLoadCacheNow('went-offline').catch(err => this.logger.error('[RemoteAppHtml5] Cache load failed (offline)', err));
    };

    constructor(
        private stomp: R2CloudStompService,
        private remoteAppDao: RemoteAppDaoService,
        private customerGroupService: CustomerGroupService,
        private pdfPrintService: PdfPrintService,
        private remoteAppReadyStoreService: RemoteAppReadyStoreService,
        private readonly logger: LoggerService
    ) {
        super();


        if (!navigator.onLine) {
            this.tryLoadCacheNow('startup-offline').catch(err => this.logger.error('[RemoteAppHtml5] Cache load failed (startup-offline)', err));
        }
        window.addEventListener('offline', this.handleOffline);

        this.addSubscription(
            this.stomp
                .stompTopic<RemoteAppReady[]>(`${USER_TOPIC}/remote-apps-ready`)
                .subscribe(
                    async (remoteAppReadyList: RemoteAppReady[]) => {
                        this.currentRemoteAppReadyList = remoteAppReadyList;

                        this.populateRemoteAppHtml5StatusList(
                            remoteAppReadyList,
                            this.customerGroupService.customerGroup?.id ?? 0
                        );

                        try {
                            await this.remoteAppReadyStoreService.saveSnapshot(
                                this.customerGroupService.customerGroup?.id ?? 0,
                                remoteAppReadyList
                            );
                        } catch (saveErr) {
                            this.logger.error('[RemoteAppHtml5] Failed to save snapshot to IndexedDB', saveErr);
                        }
                    },
                    async (err: any) => {
                        this.logger.error('[RemoteAppHtml5] STOMP error; falling back to cache', err);
                        try {
                            await this.tryLoadCacheNow('stomp-error');
                        } catch (cacheErr) {
                            this.logger.error('[RemoteAppHtml5] Cache fallback failed after STOMP error', cacheErr);
                        }
                    }
                )
        );

        this.addSubscription(this.pdfPrintService.stompTopic());

        this.customerGroupService.get().subscribe(customerGroup => {
            if (customerGroup && customerGroup.id !== this.customerGroupService.getLastSelectedCustomerGroupByCookie()?.id) {
                this.populateRemoteAppHtml5StatusList(this.currentRemoteAppReadyList, this.customerGroupService.customerGroup?.id ?? 0);
            }
        });
        Promise.resolve().then(() => this.tryLoadCacheNow('post-ctor').catch(err => this.logger.error('[RemoteAppHtml5] Cache load failed (post-ctor)', err)));
    }

    private resolveCustomerGroupId(): number | null {
        return this.customerGroupService?.customerGroup?.id ?? null;
    }

    private async tryLoadCacheNow(tag: string): Promise<void> {
        const gid = this.resolveCustomerGroupId();
        if (gid != null) {
            await this.loadFromCache(gid, tag);
            return;
        }

        this.customerGroupService.get().pipe(take(1)).subscribe({
            next: (g) => {
                if (g && g.id != null) {
                    void this.loadFromCache(g.id, tag);
                }
            },
            error: (err) => {
                this.logger.error('[RemoteAppHtml5] Failed to resolve customer group for cache load', err);
            }
        });
    }

    private async loadFromCache(customerGroupId: number, tag: string): Promise<void> {
        try {
            const snap = await this.remoteAppReadyStoreService.getSnapshot(customerGroupId);
            const cachedRemoteAppReadyList = snap?.data;
            // O snapshot guarda a lista COMPLETA da última emissão, então vale como fonte de verdade
            // igual à do STOMP — inclusive para remover (R2CLOUD-1251). Snapshot inexistente não é
            // lista vazia: sem lista nenhuma não se reconcilia nada.
            if (isAuthoritativeRemoteAppList(cachedRemoteAppReadyList)) {
                this.currentRemoteAppReadyList = cachedRemoteAppReadyList;
                this.populateRemoteAppHtml5StatusList(cachedRemoteAppReadyList, customerGroupId);
            }
        } catch (err) {
            // Falha de leitura não é lista vazia: não purga a lista do usuário.
            this.logger.error(`[RemoteAppHtml5] Failed to read snapshot from IndexedDB (${tag})`, err);
        }
    }

    private populateRemoteAppHtml5StatusList(remoteAppReadyList: RemoteAppReady[], customerGroupId: number): void {
        // A lista recebida é completa: app revogado (ou que deixou de ser elegível para o portal
        // HTML5) some dela e precisa sair daqui também — antes só se somava (R2CLOUD-1251).
        this.status = reconcileRemoteAppList(this.status, remoteAppReadyList, isRemoteAppHtml5Eligible).kept;
        for (const remoteAppReady of remoteAppReadyList) {
            if (isRemoteAppHtml5Eligible(remoteAppReady)) {
                const find = this.findRemoteAppHtml5OnList(remoteAppReady.remoteAppId);
                if (find) {
                    find.status = remoteAppReady.status;
                    this.emit(find);
                } else {
                    if (remoteAppReady.customerGroupId === customerGroupId) {
                        this.remoteAppDao.getOneHtml5(remoteAppReady.remoteAppId).subscribe({
                            next: (result) => {
                                if (!result) { return; }
                                // A resposta chega depois: se nesse meio-tempo o app saiu da lista
                                // corrente, adicioná-lo aqui o ressuscitaria na tela.
                                if (!this.isRemoteAppStillAllowed(remoteAppReady.remoteAppId)) { return; }
                                result.status = remoteAppReady.status;
                                this.status.push(result);
                                this.emit(result);
                            },
                            error: (err) => {
                                this.logger.error('[RemoteAppHtml5] Failed to load RemoteAppHtml5', err);
                            }
                        });
                    }
                }
            }
        }
    }

    /** O app ainda consta na última lista completa recebida do servidor? */
    private isRemoteAppStillAllowed(remoteAppId: number): boolean {
        return collectAllowedRemoteAppIds(this.currentRemoteAppReadyList, isRemoteAppHtml5Eligible).has(remoteAppId);
    }

    private findRemoteAppHtml5OnList(remoteAppId: number): RemoteAppHtml5 | undefined {
        return this.status.find((value) => value.remoteAppId === remoteAppId);
    }

    remoteAppChanges(): Observable<RemoteAppHtml5> {
        return new Observable<RemoteAppHtml5>((observer: Observer<RemoteAppHtml5>) => {
            const r2CloudClientSubscriber = new R2CloudClientSubscriber<RemoteAppHtml5>(observer);
            this.subscribers.push(r2CloudClientSubscriber);
            this.next(observer);
            return () => {
                this.subscribers = this.subscribers.filter((value) => r2CloudClientSubscriber.id !== value.id);
                observer.complete();
            };
        });
    }

    private next(observer: Observer<RemoteAppHtml5>): void {
        for (const status of this.status) {
            observer.next(status);
        }
    }

    private emit(status: RemoteAppHtml5): void {
        for (const subscriber of this.subscribers) {
            subscriber.observer.next(status);
        }
    }

    override ngOnDestroy(): void {
        window.removeEventListener('offline', this.handleOffline);
        for (const subscriber of this.subscribers) {
            subscriber.observer.complete();
        }
        super.ngOnDestroy();
    }
}
